/**
 * Manual theme control. `dark:` is now class-based (.dark on <html>); this
 * applies the user's choice and defaults to the OS preference for 'system'.
 * Bootstrap runs before React renders to avoid a flash of the wrong theme.
 */
export type ThemeMode = "light" | "dark" | "system";

const KEY = "klyra.theme";

export function getThemeMode(): ThemeMode {
	const v =
		typeof localStorage !== "undefined" ? localStorage.getItem(KEY) : null;
	return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function systemPrefersDark(): boolean {
	return (
		typeof matchMedia !== "undefined" &&
		matchMedia("(prefers-color-scheme: dark)").matches
	);
}

function applyResolved(mode: ThemeMode): void {
	const dark = mode === "dark" || (mode === "system" && systemPrefersDark());
	document.documentElement.classList.toggle("dark", dark);
}

export function setThemeMode(mode: ThemeMode): void {
	try {
		localStorage.setItem(KEY, mode);
	} catch {
		// ignore
	}
	applyResolved(mode);
}

let mediaBound = false;

/** Apply the stored theme + keep 'system' in sync with OS changes. Call once. */
export function bootstrapTheme(): void {
	applyResolved(getThemeMode());
	if (mediaBound || typeof matchMedia === "undefined") return;
	mediaBound = true;
	matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
		if (getThemeMode() === "system") applyResolved("system");
	});
}
